import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCheck, Power, PowerOff } from "lucide-react";

const blank = () => ({ first_name: "", last_name: "", title: "", email: "", phone: "" });

/**
 * Admin-only master team directory. Reusable across every client's Agency
 * Reps section. Edits propagate downstream when "Sync to linked reps" is on.
 */
export default function MasterTeamMembers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [cascade, setCascade] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team-members");
      setList(data || []);
    } finally { setLoading(false); }
  };
  // Mount-only initial load — subsequent refreshes are user-triggered.
  useEffect(() => { refresh(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const startEdit = (m) => {
    setEditing(m);
    setForm({
      first_name: m.first_name, last_name: m.last_name || "",
      title: m.title || "", email: m.email || "", phone: m.phone || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/team-members/${editing.team_member_id}?cascade=${cascade}`, form);
        toast.success(cascade
          ? "Team member updated · linked reps synced"
          : "Team member updated");
      } else {
        await api.post("/team-members", form);
        toast.success("Team member added");
      }
      setOpen(false); setEditing(null); setForm(blank()); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/team-members/${m.team_member_id}`, { active: !m.active });
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/team-members/${m.team_member_id}`);
      toast.success("Removed"); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500" data-testid="master-team-loading">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="master-team-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-overline">Manage</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Team members</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            Master directory of Iceberg Digital staff. Pick from this list when adding
            people to a client&apos;s Agency Reps section.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={startCreate}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-10"
              data-testid="add-team-member">
              <Plus className="h-4 w-4 mr-1" /> Add team member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-sm" data-testid="team-member-dialog">
            <DialogHeader>
              <DialogTitle className="font-display font-bold text-2xl tracking-tight">
                {editing ? "Edit team member" : "New team member"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required value={form.first_name}
                  onChange={(v) => setForm({ ...form, first_name: v })}
                  testid="tm-firstname" />
                <Field label="Last name" value={form.last_name}
                  onChange={(v) => setForm({ ...form, last_name: v })}
                  testid="tm-lastname" />
              </div>
              <Field label="Title" value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                testid="tm-title" placeholder="Marketing Director" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" type="email" value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  testid="tm-email" />
                <Field label="Phone" value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  testid="tm-phone" />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-xs text-zinc-600"
                  data-testid="tm-cascade-row">
                  <input type="checkbox" checked={cascade}
                    onChange={(e) => setCascade(e.target.checked)} />
                  Also update this person on every client where they&apos;re already a Rep.
                </label>
              )}
              <DialogFooter>
                <Button type="submit" disabled={busy}
                  className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="tm-submit">
                  {busy ? "Saving…" : (editing ? "Save changes" : "Add member")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="grid grid-cols-[2fr_2fr_2fr_1fr_auto] gap-4 px-5 py-3 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <div>Name</div>
          <div>Title</div>
          <div>Email · Phone</div>
          <div>Status</div>
          <div></div>
        </div>
        <CardContent className="p-0">
          {list.length === 0 ? (
            <div className="p-12 text-center">
              <UserCheck className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg">No team members yet</div>
              <p className="text-sm text-zinc-500 mt-1">Add the people who work on client accounts.</p>
            </div>
          ) : list.map((m) => (
            <div key={m.team_member_id}
              className="grid grid-cols-[2fr_2fr_2fr_1fr_auto] gap-4 px-5 py-3 border-b border-zinc-100 last:border-b-0 items-center"
              data-testid={`tm-row-${m.team_member_id}`}>
              <div className="font-display font-bold text-sm truncate">
                {m.first_name} {m.last_name}
              </div>
              <div className="text-sm text-zinc-700 truncate">{m.title || "—"}</div>
              <div className="text-[11px] font-mono text-zinc-500 truncate">
                {m.email || "—"} · {m.phone || "—"}
              </div>
              <div>
                <Badge variant="outline"
                  className={`rounded-sm text-[9px] font-mono ${
                    m.active ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                             : "border-zinc-300 text-zinc-500 bg-zinc-50"
                  }`}>
                  {m.active ? "active" : "inactive"}
                </Badge>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
                  title={m.active ? "Set inactive" : "Set active"}
                  onClick={() => toggleActive(m)}
                  data-testid={`tm-toggle-${m.team_member_id}`}>
                  {m.active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
                  onClick={() => startEdit(m)}
                  data-testid={`tm-edit-${m.team_member_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <DeleteButton onConfirm={() => remove(m)}
                  testid={`tm-delete-${m.team_member_id}`}
                  label={`${m.first_name} ${m.last_name}`} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="label-overline">{label}</Label>
      <Input type={type} required={required} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm" data-testid={`${testid}-input`} />
    </div>
  );
}

function DeleteButton({ onConfirm, testid, label }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost"
          className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700"
          data-testid={testid}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle>Remove {label}?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600">
          They&apos;ll be removed from the master list. Existing agency reps on
          client pages will stay (just unlinked from this master entry).
        </p>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm"
            onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
            onClick={() => { onConfirm(); setOpen(false); }}
            data-testid={`${testid}-confirm`}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
